import { Fragment, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import FormCertificate, { CertificateForm } from 'front-office/components/released-services/form';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonCertificateCrumbs } from '../constants';
import { toast } from 'react-toastify';
import ReasonVoidList from 'front-office/components/released-services/form/reason-void';
import { Grid } from '@mui/material';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformCertificate } from 'front-office/components/released-services/utils/transform-certificate';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ReleasedService } from 'shell/models/certificate';
import { tranformCertificatePdfWithourJustificationRequest } from '../utils/request-pdf-certificate-without-justification';
import { blobToBase64 } from 'shell/utils/base64-to-blob';

const CertificateVoidPage = (): React.ReactElement => {
  const params = useParams();

  const navigate = useNavigate();
  const { userProfile } = useUserProfile();
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });
  const [certificate] = useHttpFetch<HttpResponse<ReleasedService>, CertificateForm>(
    `${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati/${params.idSigla}/${params.idAnno}/${params.progressivo}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformCertificate,
    }
  );

  const voidCertificate = useHttpClient(
    `${__CERTIFICATION_API_URL__}/certificazione/annullamento-certificato/${params.idSigla}-${params.idAnno}-${params.progressivo}`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
        data: {},
      },
    }
  );

  const getPdfCertificateWithoutJustification = useHttpClient<Blob>(
    `${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati/stampa/senza-giustificativo`,
    {
      options: {
        method: 'POST',
        errorCatching: true,
        loading: true,
      },
    }
  );

  const onSubmit = useCallback(
    (formValues: CertificateForm | undefined) => {
      (
        voidCertificate({
          data: {
            tipoGiustificativo: formValues?.justification,
          },
        }) as ResponsePromise<HttpResponse<ReleasedService>>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        if (formValues?.justification !== 'N') {
          navigate('/sportello/controllo-di-cassa');
          return;
        }

        const dataCertificate = tranformCertificatePdfWithourJustificationRequest(
          userProfile,
          formValues
        );

        (
          getPdfCertificateWithoutJustification({
            data: dataCertificate,
            responseType: 'blob',
          }) as ResponsePromise<Blob>
        ).then((response) => {
          if (response?.data) {
            const blob = new Blob([response?.data], { type: 'application/pdf' });
            blobToBase64(blob).then((base64) => {
              setPdfModal({
                data: base64.split(',')[1],
                name: 'Stampa Servizio Annullato Senza Giustificativo',
                open: true,
              });
            });
          }
        });
      });
    },
    [voidCertificate, navigate]
  );

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonCertificateCrumbs, { name: 'Annullamento Certificato' }]} />
      {certificate.isLoading || !certificate.data ? null : (
        <FormCertificate
          title="Annullamento Servizio rilasciato"
          onOk={onSubmit}
          isDelete
          onGoBack={() => {
            navigate('/sportello/controllo-di-cassa');
          }}
          isVoid
          disabled
          initialValues={certificate.data as CertificateForm}
        >
          {(form) => (
            <Fragment>
              <Grid item xs={12}>
                <ReasonVoidList
                  value={form?.values.reasonVoid || ''}
                  onChange={(value) => form?.setFieldValue('reasonVoid', value)}
                />
              </Grid>
            </Fragment>
          )}
        </FormCertificate>
      )}
      {pdfModal?.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title="Certificazione"
          onClose={() => {
            setPdfModal({ data: '', open: false, name: '' });
            navigate('/sportello/controllo-di-cassa');
          }}
        />
      ) : undefined}
    </Fragment>
  );
};

CertificateVoidPage.displayName = 'Certificate void page component';

export default CertificateVoidPage;
